import { useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { SOLUTION_DETAILS } from "@/data/solutions";
import Icon from "@/components/Icon";
import { useLead } from "@/context/LeadContext";

export default function SolutionPage() {
  const { key } = useParams();
  const navigate = useNavigate();
  const { openLead } = useLead();
  const s = SOLUTION_DETAILS[key];

  useEffect(() => {
    if (!s) { navigate("/", { replace: true }); return; }
    document.title = `${s.name} | Proactive UMS Corporate`;
    window.scrollTo(0, 0);
  }, [key, s, navigate]);

  if (!s) return null;
  const goSolutions = () => navigate("/", { state: { scrollTo: "solutions" } });

  return (
    <div className="dp" data-testid={`solution-page-${s.key}`}>
      <section className="dp-hero">
        <div className="dp-hero__bg" style={{ backgroundImage: `url(${s.image})` }} />
        <div className="dp-hero__veil" />
        <div className="container">
          <div className="dp-hero__inner">
            <button className="dp-breadcrumb" onClick={goSolutions} data-testid="sp-breadcrumb">
              <Icon name="ArrowLeft" size={14} /> Corporate Solutions
            </button>
            <span className="sp-hero__ic"><Icon name={s.icon} size={30} /></span>
            <span className="eyebrow">Corporate Solutions</span>
            <h1 className="dp-hero__title display">{s.name}</h1>
            <p className="dp-hero__tagline">{s.tagline}</p>
            <p className="dp-hero__blurb">{s.heroBlurb}</p>
            <div className="dp-hero__actions">
              <button className="btn btn--green" onClick={() => openLead("demo")} data-testid="sp-request-demo">
                Request a Demo <Icon name="ArrowRight" size={16} />
              </button>
              <button className="btn btn--outline-dark" onClick={goSolutions} data-testid="sp-back">Back to Solutions</button>
            </div>
          </div>
        </div>
      </section>

      <section className="dp-overview">
        <div className="container dp-overview__grid">
          <div className="dp-overview__copy">
            <h2 className="dp-h2">Overview</h2>
            {s.overview.map((p, i) => <p key={i} className="dp-p">{p}</p>)}
          </div>
          <aside className="dp-capabilities">
            <h3 className="dp-cap__title">Key Benefits</h3>
            <ul>
              {s.benefits.map((b, i) => (<li key={i}><Icon name="CheckCircle2" size={18} /> <span>{b}</span></li>))}
            </ul>
          </aside>
        </div>
      </section>

      <section className="dp-services">
        <div className="container">
          <div className="sec-head">
            <div className="sec-head__left">
              <h2 className="sec-head__title">Capabilities<span className="sec-head__rule" /></h2>
              <p className="sec-head__sub">Everything included in {s.name}.</p>
            </div>
          </div>
          <div className="dp-services__grid">
            {s.features.map((f) => (
              <div className="dp-service" key={f.title} data-testid={`sp-feature-${f.title}`}>
                <span className="dp-service__ic"><Icon name={f.icon} size={24} /></span>
                <h3>{f.title}</h3>
                <p>{f.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="dp-stats">
        <div className="container dp-stats__row">
          {s.stats.map((st) => (
            <div className="dp-stat" key={st.label}><strong>{st.value}</strong><span>{st.label}</span></div>
          ))}
        </div>
      </section>

      <section className="dp-cta">
        <img src="/assets/cta-family.png" className="dp-cta__bg" alt="" />
        <div className="container dp-cta__inner">
          <div className="dp-cta__copy">
            <h2 className="dp-cta__title display">See {s.name} in action.</h2>
            <p>Discover how this solution connects your organization.</p>
          </div>
          <div className="dp-cta__actions">
            <button className="btn btn--green" onClick={() => openLead("demo")} data-testid="sp-cta-demo">Request a Demo <Icon name="ArrowRight" size={16} /></button>
            <button className="btn btn--outline-dark" onClick={() => openLead("contact")} data-testid="sp-cta-contact"><Icon name="Mail" size={16} /> Contact Our Team</button>
          </div>
        </div>
      </section>

      <section className="dp-siblings">
        <div className="container">
          <h3 className="dp-siblings__title">Explore other solutions</h3>
          <div className="dp-siblings__row">
            {Object.values(SOLUTION_DETAILS).filter((x) => x.key !== key).map((x) => (
              <button key={x.key} className="dp-sibling" onClick={() => navigate(`/solutions/${x.key}`)} data-testid={`sp-sibling-${x.key}`}>
                <span className="sp-sibling__ic"><Icon name={x.icon} size={16} /></span>
                <span className="dp-sibling__name">{x.name}</span>
                <Icon name="ArrowRight" size={14} />
              </button>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
